import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import max from 'lodash/max';
import min from 'lodash/min';
import sum from 'lodash/sum';
import sumBy from 'lodash/sumBy';
import uniq from 'lodash/uniq';
import moment from 'moment';

import { businessModelValues } from '../../opportunities/constants';
import { offerTypes, OPERATIVE_SYSTEM_TYPES } from '../../products/constants';
import { isOffsite } from '../../products/utils';
import { convertToOptionInScreamingSnakeCase } from '../../utils/convert-to-option';
import {
  campaignLineStatuses,
  complianceStatuses,
  targetings,
} from '../constants';

const HOME = 'home';

export function hasNoFields(line) {
  // true unless any label
  return !(
    line.product.hasTitle ||
    line.product.hasSubTitle ||
    line.product.hasCta ||
    line.product.hasText ||
    line.product.hasLp
  );
}

export function supportsAdManager(line) {
  return line.product.supportsAdManager;
}

export function isEditable(line) {
  return ![campaignLineStatuses.PUSHED, campaignLineStatuses.LOCKED].includes(
    line.status,
  );
}

export function isSelectable(line, isDraftCampaignEnabled = false) {
  return (
    !line.filteredOut &&
    isEditable(line) &&
    (isDraftCampaignEnabled ? !supportsAdManager(line) : true)
  );
}

export function isSelected(line) {
  return line.selected;
}

export function isExpanded(line) {
  return line.expanded;
}

export function isDraft(line) {
  return line.isDraft;
}

export function isNotConfigurable(line) {
  // true unless any overwrites or any language
  return !(
    line.product.overwriteTimeRange ||
    line.product.overwriteGender ||
    line.product.overwriteCategories ||
    line.product.serviceDescription ||
    line.product.adjustedFee ||
    line.language1 ||
    line.language2 ||
    line.language3 ||
    line.language4
  );
}

export function findTypeLimits(product) {
  return {
    title: product.titleLimit || 0,
    subTitle: product.subtitleLimit || 0,
    cta: product.ctaLimit || 0,
    desc: product.textLimit || 0,
    landingPage: product.lpLimit || 0,
  };
}

export function findLanguageLineNumber(line, languageCode) {
  const languageIndex = [
    line.language1,
    line.language2,
    line.language3,
    line.language4,
  ].findIndex(lang => lang === languageCode);

  if (languageIndex < 0) {
    return null;
  }

  return languageIndex + 1;
}

export function getDurationInDays(startDate, endDate) {
  return moment(endDate).diff(startDate, 'days') + 1; // includes the last day
}

export function weightedAverage(values) {
  const sumWeights = sumBy(values, 'weight');
  return sum(
    values.map(({ value, weight }) => {
      const weighingFactor = weight / sumWeights || 0;
      return weighingFactor * value;
    }),
  );
}

export function getAverageDailyRate(onsiteCampaignLines) {
  if (!onsiteCampaignLines.length) {
    return null;
  }

  const values = onsiteCampaignLines.map(line => {
    const { spendableBudget, startDate, endDate } = line;
    const duration = getDurationInDays(startDate, endDate);
    const expectedDailyBudget = spendableBudget / duration;

    return {
      value: expectedDailyBudget,
      weight: spendableBudget,
    };
  });

  return weightedAverage(values);
}

function getSpendKey(product) {
  return isOffsite(product) ? 'offsiteSpend' : 'spend';
}

export function getSpentBudgetForBusiness(campaign, lines) {
  const productIds = uniq(lines.map(line => line.product.id));
  const relatedAggregates = campaign.productMetas.filter(({ productId }) =>
    productIds.includes(productId),
  );
  const spendKey = getSpendKey(lines[0].product);
  return sumBy(relatedAggregates, spendKey) || 0;
}

export function getSpentBudgetForProduct(campaign, product) {
  const aggregate = campaign.productMetas.find(
    ({ productId: pId }) => pId === product.id,
  );

  return get(aggregate, getSpendKey(product), 0);
}

export function getBudgetTotals(lines = []) {
  return lines.reduce(
    (accumulator, line) => {
      return {
        budget: line.spendableBudget + accumulator.budget,
        netClientBudget: line.netClientBudget + accumulator.netClientBudget,
        netClientBudgetExternal:
          line.netClientBudgetExternal + accumulator.netClientBudgetExternal,
        wkzBudget: line.wkzBudget + accumulator.wkzBudget,
        coInvest: line.coInvest + accumulator.coInvest,
        freeService: line.freeService + accumulator.freeService,
      };
    },
    {
      budget: 0,
      netClientBudget: 0,
      netClientBudgetExternal: 0,
      wkzBudget: 0,
      coInvest: 0,
      freeService: 0,
    },
  );
}

export function getMinStartDate(lines) {
  return min(lines.map(ea => ea.startDate));
}

export function getMaxEndDate(lines) {
  return max(lines.map(ea => ea.endDate));
}

export function getLinesForProduct(product, campaignLinesById) {
  const { countries } = product;
  return flatMap(countries, 'lines').map(id => campaignLinesById[id]);
}

export function getLinesForBusiness(business, campaignLinesById) {
  const { products } = business;
  return flatMap(products, product =>
    getLinesForProduct(product, campaignLinesById),
  );
}

export const overwriteConfigMap = {
  categories: 'overwriteCategories',
  genders: 'overwriteGender',
  startDate: 'overwriteTimeRange',
  endDate: 'overwriteTimeRange',
  title: 'hasTitle',
  subTitle: 'hasSubTitle',
  desc: 'hasText',
  cta: 'hasCta',
  landingPage: 'hasLp',
};

export function isChangeRestricted(line, prop) {
  const configKey = overwriteConfigMap[prop];
  if (!configKey) {
    return false;
  }
  return !line.product[configKey];
}

export function getPartiallyUpdatedLPUrl(currentUrl, name, value) {
  try {
    const { pathname = '', origin = '', search = '' } = new URL(currentUrl);
    if (name === 'landingPageOrigin') {
      return `${value}${pathname}${search}`;
    }
    if (name === 'landingPagePathname') {
      return `${origin}/${value}`;
    }
    if (name === 'landingPageFilter' && pathname === '/') {
      return `${origin}`;
    }
    if (name === 'landingPageFilter') {
      return `${origin}${pathname}${value}`;
    }
    return '';
  } catch (e) {
    return currentUrl;
  }
}

export function replacedBrandPlaceholders(str, brand) {
  const replaceObj = {
    '{brand_name}': brand.name,
    '{brand_code}': brand.code,
  };
  return str.replace(
    /{brand_name}|{brand_code}/gi,
    matched => replaceObj[matched],
  );
}

export function getProductMetaFieldName(index, name) {
  return `productMetas[${index}].${name}`;
}

export function getOpsHandoverCompliance(lines = []) {
  let opsHandoverCompliance = complianceStatuses.NONE;
  let isCompliant = true;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (line.opsHandoverCompliance === complianceStatuses.NON_COMPLIANT) {
      opsHandoverCompliance = complianceStatuses.NON_COMPLIANT;
      isCompliant = false;
      break;
    }

    isCompliant =
      isCompliant &&
      line.opsHandoverCompliance === complianceStatuses.COMPLIANT;
  }

  if (isCompliant) {
    opsHandoverCompliance = complianceStatuses.COMPLIANT;
  }

  return opsHandoverCompliance;
}

export const isPushedToCCP = campaignLinesById =>
  campaignLinesById.some(
    campaignLine =>
      campaignLine.product.operativeSystem ===
        OPERATIVE_SYSTEM_TYPES.ccp.system &&
      campaignLine.status === campaignLineStatuses.PUSHED,
  );

export const isPushedToCF = campaignLinesById =>
  campaignLinesById.some(
    campaignLine =>
      campaignLine.product.operativeSystem ===
        OPERATIVE_SYSTEM_TYPES.creativeFactory.system &&
      campaignLine.status === campaignLineStatuses.PUSHED,
  );

export const checkForHPTO = campaignLinesById =>
  campaignLinesById.some(
    campaignLine => campaignLine.offerType === offerTypes.HPTO,
  );

export const checkForCountdown = campaignLinesById =>
  campaignLinesById.some(campaignLine =>
    campaignLine.product.name.includes('Countdown'),
  );

export const getFormattedDate = (date, dateFormat) =>
  moment(date).format(dateFormat);

export const checkZmsAmOperativeSystem = (campaign, lines) => {
  const { businessModel } = campaign.opportunity;
  const { merchants } = campaign;
  const { hasCCPCampaignLevelSteering } = campaign.opportunity;

  const isWholesaleBusinessModel =
    businessModel === businessModelValues.WHOLESALE;

  const startDates = uniq(lines.map(item => item.startDate));
  const endDates = uniq(lines.map(item => item.endDate));
  const countryCodes = lines.map(item => item.countryCode);

  const hasDuplicateCountryCodes = countryCodes.some((element, index) => {
    return countryCodes.indexOf(element) !== index;
  });

  const isSameDates = startDates.length === 1 && endDates.length === 1;

  if (isWholesaleBusinessModel) {
    if (hasCCPCampaignLevelSteering) {
      return isSameDates && !hasDuplicateCountryCodes;
    }
    return !hasDuplicateCountryCodes;
  }

  const isZmsAmBusinessModel = [
    businessModelValues.PARTNER_PROGRAM,
    businessModelValues.CONNECTED_RETAIL,
    businessModelValues.HYBRID,
  ].includes(businessModel);

  const areAllMerchantsZmsAm = merchants.every(
    merchant => merchant.eligibleForZMSAMPush,
  );

  const baseCondition =
    isZmsAmBusinessModel && areAllMerchantsZmsAm && !hasDuplicateCountryCodes;

  return hasCCPCampaignLevelSteering
    ? baseCondition && isSameDates
    : baseCondition;
};

export const targetingOptions = ({ externalName }) => {
  const { THEMED_HOME, ...noHomeProductsTargetings } = targetings;
  const options = externalName.toLowerCase().includes(HOME)
    ? targetings
    : noHomeProductsTargetings;

  return Object.entries(options).map(convertToOptionInScreamingSnakeCase);
};

export const targetingOptionsForBulkEditor = campaignLines => {
  const productsSupportingTargeting = campaignLines
    .map(({ product }) => product)
    .filter(({ supportsTargeting }) => supportsTargeting);

  if (productsSupportingTargeting.length === 0) return [];

  const isAnyProductNotHome = productsSupportingTargeting.some(
    item => !item.externalName.toLowerCase().includes(HOME),
  );
  const { THEMED_HOME, ...noHomeProductsTargetings } = targetings;
  const options = isAnyProductNotHome ? noHomeProductsTargetings : targetings;
  const transformedOptions = Object.entries(options).map(
    convertToOptionInScreamingSnakeCase,
  );

  return transformedOptions;
};
